<script setup lang="ts">
// icons
import { faThumbsUp } from '@fortawesome/pro-solid-svg-icons';

/********************
 * PROPS & EMITS     *
 ********************/
export interface CiReviewFooterProps {
  review: any;
}
const props = withDefaults(defineProps<CiReviewFooterProps>(), {});

const emit = defineEmits<{
  markedAsHelpful: [];
}>();

/********************
 * COMPOSITIONS      *
 ********************/
const appStore = useAppStore();
const localePath = useLocalePath();
const reviewService = useReviewService();
const route = useRoute();
const userStore = useUserStore();
const { $gettext, $ngettext, interpolate } = useGettext();
const { locale } = useI18n();

/********************
 * REFS & VARS       *
 ********************/
const requestIsResolved = ref(false);
const voteIsPending = ref(false);

const buttonText = computed(() => {
  return $gettext('Helpful!');
});

const helpfulText = computed(() => {
  if (isUserReview.value) {
    const translated = $ngettext(
      '%{count} camper finds your rating helpful.',
      '%{count} campers find your rating helpful.',
      props.review.helpful,
    );
    return interpolate(translated, { count: props.review.helpful });
  }

  if (!props.review.is_rated_by_user && props.review.helpful === 0) {
    return $gettext('No camper finds this rating helpful, yet. Be the first!');
  }

  if (!props.review.is_rated_by_user) {
    const translated = $ngettext(
      '%{count} camper finds this rating helpful.',
      '%{count} campers find this rating helpful.',
      props.review.helpful,
    );
    return interpolate(translated, { count: props.review.helpful });
  }

  if (props.review.is_rated_by_user && props.review.helpful >= 2) {
    const translated = $ngettext(
      'You and %{count} camper find this rating helpful.',
      'You and %{count} campers find this rating helpful.',
      props.review.helpful - 1,
    );
    return interpolate(translated, { count: props.review.helpful - 1 });
  }

  return $gettext('You find this review helpful.');
});

const isUserReview = computed(() => {
  return (
    props.review.created_by && userStore.user.profile?.uid && props.review.created_by === userStore.user.profile.uid
  );
});

const userVoted = computed(() => {
  return props.review.is_rated_by_user;
});

const canMarkAsHelpful = computed(() => {
  if (appStore.permissions?.mark_review_as_useful && !appStore.permissions?.mark_review_as_useful?.has_permission) {
    return false;
  }

  return true;
});

/********************
 * FUNCTIONS         *
 ********************/
function onVoteHelpful(review) {
  if (!userStore.loggedIn) {
    const path = `${route.path.replace(`/${route.params.id}`, '')}/${review.id}`;
    return navigateTo(
      localePath({
        name: RBN_LOGIN,
        query: { redirect: path },
      }),
    );
  }

  voteIsPending.value = true;

  reviewService
    .vote(locale.value, { review: props.review.id })
    .then(() => {
      voteIsPending.value = false;
      requestIsResolved.value = true;
      emit('markedAsHelpful');
      setTimeout(() => {
        requestIsResolved.value = false;
      }, 1500);
    })
    .catch(() => {
      voteIsPending.value = false;
    });
}
</script>

<template>
  <div class="row my-0 flex items-center text-sm lg:my-2">
    <div class="col-12 col-lg-9 flex items-center justify-center py-4 text-dark lg:justify-start">
      <CiAwesomeIcon
        :icon="faThumbsUp"
        class="mr-2 fill-dark"
        ratio="0.5"
      /><span>{{ helpfulText }}</span>
    </div>
    <div
      v-if="!props.review.is_rated_by_user && !isUserReview && !userVoted && !voteIsPending && canMarkAsHelpful"
      class="col-12 col-md-6 offset-md-3 col-lg-3 offset-lg-0 mb-2 lg:mb-0"
    >
      <CiButton
        tag="button"
        variant="secondary"
        class="my-0 w-full lg:my-2"
        @click="onVoteHelpful(props.review)"
      >
        {{ buttonText }}
      </CiButton>
    </div>
    <transition name="fade">
      <div
        v-if="voteIsPending && !requestIsResolved"
        class="col-4 offset-4 col-md-2 offset-md-5 col-lg-1 offset-lg-2 mb-2 text-center lg:mb-0 lg:text-right"
      >
        <CiAnimatedLoadingIndicator class="mx-auto max-w-12" />
      </div>
    </transition>
    <transition name="fade">
      <div
        v-if="!voteIsPending && requestIsResolved"
        class="col-4 offset-4 col-md-2 offset-md-5 col-lg-1 offset-lg-2 mb-2 text-center lg:mb-0 lg:text-right"
      >
        <CiAnimatedCheck class="max-w-12" />
      </div>
    </transition>
  </div>
</template>
